import { MaintenanceRequestSearchParamsDTO, MaintenanceSearchField } from '@inosio/payments-sdk';
import { Button, Divider, Grid, IconButton, Pagination, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { format } from 'date-fns';
import { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddCardIcon from '../../../icons/addcard.png';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { selectResidentDetails } from '../../payments/sagas/residentpaymentsSlice';
import { getInProgressMaintenanceRequests, selectMaintenanceState } from '../sagas/MaintenanceSlice';
import CreateMaintenanceRequestModal from './CreateMaintenanceRequestModal';
import ViewMaintenanceDetailsModal from './ViewMaintenanceDetailsModal';
import MaintenanceTile from '../../../icons/MaintenanceTile.svg';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

export default function MaintenanceRequestTile(): ReactElement {
  const [openCreateMrModal, setOpenCreateMrModal] = useState(false);
  const [openViewMrModal, setOpenViewMrModal] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const dispatch = useDispatch();
  const residentDetails = useSelector(selectResidentDetails);
  const { InProgressMaintenanceRequestFetchResponseDto, isFetchingInProgressMaintenanceRequests } = useSelector(selectMaintenanceState);
  const { propertyGroupDetails: PropertyGroupDetails } = useSelector(selectPropertyGroupDetailsState);


  const handleChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPageNumber(page - 1);
  };

  useEffect(() => {
    if (residentDetails) {
      dispatch({
        type: getInProgressMaintenanceRequests.type,
        payload: {
          requesterUuid: residentDetails?.Uuid,
          params: {
            SearchField: MaintenanceSearchField.Status,
            SearchValue: ['In Progress', 'Submitted'],
          } as MaintenanceRequestSearchParamsDTO,
        },
      });
    }
  }, [dispatch, residentDetails]);

  const fontStyle = {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    color: '#B4B4B4',
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Grid container>
        <Grid item xs={12} display="flex" justifyContent="space-between">
          <Typography id="tableTitleMaintenance">Maintenance Requests</Typography>
          {PropertyGroupDetails?.MaintenanceEnabled && (
            <IconButton onClick={() => setOpenCreateMrModal(true)}>
              <img style={{ cursor: 'pointer', width: '20px', height: '20px' }} src={AddCardIcon} alt="add card" />
            </IconButton>
          )}
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {isFetchingInProgressMaintenanceRequests ? (
          <Grid item xs={12} height="150px" display="flex" justifyContent="center" alignItems="center">
            <SpinnerWithNoConfirmation />
          </Grid>
        ) : InProgressMaintenanceRequestFetchResponseDto?.Rows?.length > 0 ? (
          <Grid
            item
            xs={12}
            sx={{
              height: 150,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: 7,
              },
              '&::-webkit-scrollbar-track': {
                boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                borderRadius: '24px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'darkgrey',
                borderRadius: '24px',
              },
            }}
          >
            <Grid mt={1} item xs={12} display="flex" alignItems="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '16px',
                  color: '#14142B',
                  marginRight: '10px',
                }}
              >
                {InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.Title}
              </Typography>
              <Typography sx={fontStyle}>{InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.CreatedDate && format(new Date(InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.CreatedDate), 'MM/dd/yyyy')}</Typography>
            </Grid>
            <Grid item xs={12} mb={0.5}>
              <Typography sx={fontStyle}>{InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.CurrentStatus}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#14142B',
                }}
              >
                {InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.Description}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12} height="150px" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <img src={MaintenanceTile} alt="NoRequests" />
            <Typography
              textAlign="center"
              sx={{
                fontFamily: 'Work Sans',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                color: '#14142B',
                marginTop: '2px',
              }}
            >
              In progress maintenance requests will be shown here
            </Typography>
          </Grid>
        )}
        {InProgressMaintenanceRequestFetchResponseDto?.Rows?.length > 0 ? (
          <Grid item xs={12} display="flex" justifyContent="space-between">
            <Pagination count={InProgressMaintenanceRequestFetchResponseDto?.TotalCount} size="small" boundaryCount={0} siblingCount={0} onChange={handleChange} />
            <Button sx={{ height: '30px', width: '100px' }} variant="outlined" id="secondary-axn-btn" onClick={() => setOpenViewMrModal(true)}>
              view
            </Button>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
      <CreateMaintenanceRequestModal open={openCreateMrModal} onClose={() => setOpenCreateMrModal(false)} />
      <ViewMaintenanceDetailsModal open={openViewMrModal} onClose={() => setOpenViewMrModal(false)} title={InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.Title} statusHistory={InProgressMaintenanceRequestFetchResponseDto?.Rows?.[pageNumber]?.StatusHistory} />
    </Box>
  );
}
